import React from 'react';
import PropTypes from 'prop-types';
import { Card } from './BaseComponents';

/**
 * ContentSection Component
 * 
 * A reusable section wrapper with optional shadow and background.
 * Replaces: <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
 * 
 * Usage:
 * <ContentSection title="Settings" icon="⚙️">
 *   <p>Section content</p>
 * </ContentSection>
 */
export const ContentSection = ({
  children,
  title = null,
  icon = null,
  subtitle = null,
  action = null,
  padding = 'md',
  className = ''
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow theme-transition ${paddingClasses[padding]} ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          <div>
            {title && (
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {icon && <span>{icon}</span>}
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

ContentSection.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  icon: PropTypes.string,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * FormSection Component
 * 
 * Vertical spacing for form fields.
 * Replaces: <div className="space-y-4">
 * 
 * Usage:
 * <FormSection>
 *   <Input ... />
 *   <Input ... />
 * </FormSection>
 */
export const FormSection = ({
  children,
  spacing = 'md', // 'sm', 'md', 'lg'
  className = ''
}) => {
  const spacingClasses = {
    sm: 'space-y-3',
    md: 'space-y-4',
    lg: 'space-y-6'
  };

  return (
    <div className={`${spacingClasses[spacing]} ${className}`}>
      {children}
    </div>
  );
};

FormSection.propTypes = {
  children: PropTypes.node.isRequired,
  spacing: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * TwoColumnLayout Component
 * 
 * Responsive two-column grid layout.
 * Replaces: <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 * 
 * Usage:
 * <TwoColumnLayout>
 *   <ContentSection>Left</ContentSection>
 *   <ContentSection>Right</ContentSection>
 * </TwoColumnLayout>
 */
export const TwoColumnLayout = ({
  children,
  gap = 'md',
  breakpoint = 'lg', // 'md', 'lg'
  className = ''
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8'
  };

  const breakpointClasses = {
    md: 'md:grid-cols-2',
    lg: 'lg:grid-cols-2'
  };

  return (
    <div className={`grid grid-cols-1 ${breakpointClasses[breakpoint]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

TwoColumnLayout.propTypes = {
  children: PropTypes.node.isRequired,
  gap: PropTypes.oneOf(['sm', 'md', 'lg']),
  breakpoint: PropTypes.oneOf(['md', 'lg']),
  className: PropTypes.string
};

/**
 * ThreeColumnLayout Component
 * 
 * Responsive three-column grid layout.
 * 
 * Usage:
 * <ThreeColumnLayout>
 *   <StatCard ... />
 *   <StatCard ... />
 *   <StatCard ... />
 * </ThreeColumnLayout>
 */
export const ThreeColumnLayout = ({
  children,
  gap = 'md',
  className = ''
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8'
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

ThreeColumnLayout.propTypes = {
  children: PropTypes.node.isRequired,
  gap: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * ActionButtonGroup Component
 * 
 * Responsive button grid for quick actions.
 * Replaces: <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 * 
 * Usage:
 * <ActionButtonGroup>
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </ActionButtonGroup>
 */
export const ActionButtonGroup = ({
  children,
  columns = 4, // 2, 3, 4
  gap = 'sm',
  className = ''
}) => {
  const columnClasses = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4'
  };

  const gapClasses = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6'
  };

  return (
    <div className={`grid grid-cols-1 ${columnClasses[columns]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

ActionButtonGroup.propTypes = {
  children: PropTypes.node.isRequired,
  columns: PropTypes.oneOf([2, 3, 4]),
  gap: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * StackedList Component
 * 
 * Vertical list with consistent spacing.
 * Replaces: <div className="space-y-3">
 * 
 * Usage:
 * <StackedList>
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </StackedList>
 */
export const StackedList = ({
  children,
  spacing = 'sm',
  className = ''
}) => {
  const spacingClasses = {
    sm: 'space-y-3',
    md: 'space-y-4',
    lg: 'space-y-6'
  };

  return (
    <div className={`${spacingClasses[spacing]} ${className}`}>
      {children}
    </div>
  );
};

StackedList.propTypes = {
  children: PropTypes.node.isRequired,
  spacing: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

export default {
  ContentSection,
  FormSection,
  TwoColumnLayout,
  ThreeColumnLayout,
  ActionButtonGroup,
  StackedList
};
